import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import './PaymentPage.css';

// Import images directly
import breadcrumbBg from '../../assets/img/bg/breadcrumb-bg.png';
import cloudIcon from '../../assets/img/icons/cloud.png';
import balloonIcon from '../../assets/img/icons/ballon-sclation.png';

const PaymentPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [enquiry, setEnquiry] = useState(null);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState('');
    const [formData, setFormData] = useState({
        refNo: '',
        cardNumber: '',
        expiryDate: '',
        cvv: '',
        cardHolder: '',
        paymentMethod: 'card'
    });
    const [formErrors, setFormErrors] = useState({});

    useEffect(() => {
        if (!id) {
            setError('Invalid payment link: No enquiry ID provided');
            setLoading(false);
            return;
        }
        fetchEnquiryDetails();
    }, [id]);

    const fetchEnquiryDetails = async () => {
        try {
            setLoading(true);
            console.log(' PaymentPage: Starting to fetch enquiry with ID:', id);

            const apiUrl = `http://localhost:5000/api/enquiries/${id}`;
            console.log('🔗 Fetching from:', apiUrl);

            const response = await fetch(apiUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (response.ok) {
                const data = await response.json();
                console.log('Enquiry data received:', data);
                setEnquiry(data);

                if (data.payment_active === 1) {
                    setError('This payment has already been completed.');
                }
            } else {
                console.error(' HTTP Error - Status:', response.status);
                let errorMessage = 'Failed to load enquiry';

                if (response.status === 404) {
                    errorMessage = 'Enquiry not found. Please check the payment link.';
                } else if (response.status === 500) {
                    errorMessage = 'Server error. Please try again later.';
                }

                setError(errorMessage);
                setEnquiry(null);

                try {
                    const errorData = await response.json();
                    console.error('Error response:', errorData);
                } catch (e) {
                    console.error(' Could not parse error response');
                }
            }
        } catch (err) {
            console.error(' Network error:', err);
            setError('Network error. Please check if the server is running on port 5000.');
            setEnquiry(null);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        if (enquiry?.payment_active === 1) return;

        const { name, value } = e.target;

        if (name === 'cardNumber') {
            const formattedValue = value.replace(/\s/g, '').replace(/(\d{4})/g, '$1 ').trim();
            setFormData(prev => ({ ...prev, [name]: formattedValue }));
        }
        else if (name === 'expiryDate') {
            const formattedValue = value.replace(/\D/g, '').replace(/(\d{2})(\d)/, '$1/$2').substring(0, 5);
            setFormData(prev => ({ ...prev, [name]: formattedValue }));
        }
        else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }

        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const errors = {};

        if (!formData.refNo.trim()) {
            errors.refNo = 'Reference number is required';
        }

        if (formData.paymentMethod === 'card') {
            if (!formData.cardNumber.trim()) {
                errors.cardNumber = 'Card number is required';
            } else if (formData.cardNumber.replace(/\s/g, '').length !== 16) {
                errors.cardNumber = 'Card number must be 16 digits';
            }

            if (!formData.expiryDate.trim()) {
                errors.expiryDate = 'Expiry date is required';
            } else if (!/^\d{2}\/\d{2}$/.test(formData.expiryDate)) {
                errors.expiryDate = 'Invalid expiry date format (MM/YY)';
            }

            if (!formData.cvv.trim()) {
                errors.cvv = 'CVV is required';
            } else if (!/^\d{3,4}$/.test(formData.cvv)) {
                errors.cvv = 'CVV must be 3 or 4 digits';
            }

            if (!formData.cardHolder.trim()) {
                errors.cardHolder = 'Card holder name is required';
            }
        }

        return errors;
    };

    const handlePayment = async (e) => {
        e.preventDefault();

        if (enquiry?.payment_active === 1) {
            alert('This payment has already been completed.');
            return;
        }

        const errors = validateForm();
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        setProcessing(true);

        try {
            const response = await fetch(`/api/enquiries/${id}/payment`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    ref_no: formData.refNo,
                    payment_method: formData.paymentMethod
                })
            });

            const data = await response.json();

            if (response.ok) {
                alert('Payment completed successfully!');
                navigate('/payment-success', {
                    state: {
                        enquiry: enquiry,
                        refNo: formData.refNo
                    }
                });
            } else {
                alert(data.message || 'Error processing payment');
            }
        } catch (err) {
            alert('Network error. Please try again.');
        } finally {
            setProcessing(false);
        }
    };

    const isAlreadyPaid = enquiry?.payment_active === 1;
    const isButtonDisabled = processing || isAlreadyPaid;

    if (loading) {
        return (
            <div className="payment-loading">
                <div className="spinner-border text-theme" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p>Loading payment details...</p>
            </div>
        );
    }

    if (error || !enquiry) {
        return (
            <div className="payment-error">
                <div className="container text-center">
                    <h2>{error || 'Enquiry Not Found'}</h2>
                    <p>The payment link is invalid or has expired.</p>
                    <p>Enquiry ID: {id}</p>
                    <Link to="/" className="vs-btn">Return to Home</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="payment-page">
            {/* Breadcrumb Section */}
            <section
                className="vs-breadcrumb payment-breadcrumb"
                style={{ backgroundImage: `url(${breadcrumbBg})` }}
            >
                <img
                    src={cloudIcon}
                    alt="cloud icon"
                    className="vs-breadcrumb-icon-1 animate-parachute"
                />
                <img
                    src={balloonIcon}
                    alt="balloon icon"
                    className="vs-breadcrumb-icon-2 animate-parachute"
                />
                <div className="container">
                    <div className="row text-center">
                        <div className="col-12">
                            <div className="breadcrumb-content">
                                <h1 className="breadcrumb-title">
                                    {isAlreadyPaid ? 'Payment Completed' : 'Complete Your Payment'}
                                </h1>
                                <p className="breadcrumb-description">
                                    {isAlreadyPaid
                                        ? 'This payment has been successfully processed'
                                        : 'Secure payment for your travel enquiry'
                                    }
                                </p>
                            </div>
                            <div className="breadcrumb-menu">
                                <ul className="custom-ul">
                                    <li>
                                        <Link to="/">Home</Link>
                                    </li>
                                    <li>Payment</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Rest of your JSX remains the same */}
            <section className="vs-payment space">
                <div className="container">
                    <div className="row g-4 gx-xl-5">
                        <div className="col-lg-5">
                            <div className="payment-summary">
                                <div className="title-area text-start mb-4">
                                    <span className="sec-subtitle style-2">Payment Summary</span>
                                    <h2 className="sec-title">Order Details</h2>
                                </div>

                                <div className="enquiry-details-card">
                                    <div className="detail-header">
                                        <h4>Enquiry #{enquiry.enquiries_pkid}</h4>
                                        <span className={`status-badge ${enquiry.payment_active ? 'paid' : 'pending'}`}>
                                            {enquiry.payment_active ? 'Paid' : 'Pending Payment'}
                                        </span>
                                    </div>

                                    <div className="detail-content">
                                        <div className="detail-row">
                                            <span className="label">Customer Name:</span>
                                            <span className="value">{enquiry.name}</span>
                                        </div>
                                        <div className="detail-row">
                                            <span className="label">Email:</span>
                                            <span className="value">{enquiry.email}</span>
                                        </div>
                                        <div className="detail-row">
                                            <span className="label">Phone:</span>
                                            <span className="value">{enquiry.phone}</span>
                                        </div>
                                        <div className="detail-row">
                                            <span className="label">Service Description:</span>
                                            <span className="value">{enquiry.description || 'No description provided'}</span>
                                        </div>
                                        <div className="detail-row amount-row">
                                            <span className="label">Total Amount:</span>
                                            <span className="value amount">₹{enquiry.amount || '0.00'}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Show payment completed message if already paid */}
                                {isAlreadyPaid && (
                                    <div className="payment-completed-alert">
                                        <div className="alert alert-success">
                                            <i className="fa-solid fa-check-circle me-2"></i>
                                            <strong>Payment Completed</strong>
                                            <p className="mb-0 mt-2">
                                                This payment was successfully processed on {enquiry.payment_date || 'a previous date'}.
                                                Reference: {enquiry.ref_no || 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                <div className="payment-security">
                                    <div className="security-info">
                                        <i className="fa-solid fa-shield-check"></i>
                                        <div className="security-text">
                                            <h6>Secure Payment</h6>
                                            <p>Your payment information is encrypted and secure</p>
                                        </div>
                                    </div>
                                    <div className="accepted-cards">
                                        <span>We Accept:</span>
                                        <div className="card-icons">
                                            <i className="fa-brands fa-cc-visa"></i>
                                            <i className="fa-brands fa-cc-mastercard"></i>
                                            <i className="fa-brands fa-cc-amex"></i>
                                            <i className="fa-brands fa-cc-paypal"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Payment Form */}
                        <div className="col-lg-7">
                            <div className="payment-form-container">
                                <div className="title-area text-start mb-4">
                                    <span className="sec-subtitle style-2">payment method</span>
                                    <h2 className="sec-title">
                                        {isAlreadyPaid ? 'Payment Status' : 'Complete Payment'}
                                    </h2>
                                </div>

                                {isAlreadyPaid ? (
                                    <div className="payment-completed-message">
                                        <div className="success-icon">
                                            <i className="fa-solid fa-check-circle"></i>
                                        </div>
                                        <h4>Payment Already Completed</h4>
                                        <p>This enquiry has already been paid. No further payment is required.</p>
                                        <div className="completed-details">
                                            <p><strong>Reference Number:</strong> {enquiry.ref_no || 'N/A'}</p>
                                            <p><strong>Status:</strong> <span className="text-success">Paid</span></p>
                                        </div>
                                        <Link to="/" className="vs-btn mt-3">
                                            Return to Homepage
                                        </Link>
                                    </div>
                                ) : (
                                    <form className="form-style1 payment-form" onSubmit={handlePayment}>
                                        {/* Payment Method Selection */}
                                        <div className="form-group mb-4">
                                            <label className="form-label">Payment Method</label>
                                            <div className="payment-methods">
                                                <label className="payment-method-card active">
                                                    <input
                                                        type="radio"
                                                        name="paymentMethod"
                                                        value="card"
                                                        checked={formData.paymentMethod === 'card'}
                                                        onChange={handleInputChange}
                                                        disabled={isButtonDisabled}
                                                    />
                                                    <div className="method-content">
                                                        <i className="fa-solid fa-credit-card"></i>
                                                        <span>Credit/Debit Card</span>
                                                    </div>
                                                </label>
                                                <label className="payment-method-card">
                                                    <input
                                                        type="radio"
                                                        name="paymentMethod"
                                                        value="paypal"
                                                        checked={formData.paymentMethod === 'paypal'}
                                                        onChange={handleInputChange}
                                                        disabled={isButtonDisabled}
                                                    />
                                                    <div className="method-content">
                                                        <i className="fa-brands fa-paypal"></i>
                                                        <span>PayPal</span>
                                                    </div>
                                                </label>
                                            </div>
                                        </div>

                                        {/* Reference Number */}
                                        <div className="form-group">
                                            <input
                                                name="refNo"
                                                type="text"
                                                className={`form-control ${formErrors.refNo ? 'is-invalid' : ''}`}
                                                placeholder="Payment Reference Number*"
                                                value={formData.refNo}
                                                onChange={handleInputChange}
                                                disabled={isButtonDisabled}
                                            />
                                            {formErrors.refNo && (
                                                <div className="invalid-feedback">{formErrors.refNo}</div>
                                            )}
                                        </div>

                                        {/* Card Details - Only show if card payment selected */}
                                        {formData.paymentMethod === 'card' && (
                                            <>
                                                <div className="form-group">
                                                    <input
                                                        name="cardNumber"
                                                        type="text"
                                                        className={`form-control ${formErrors.cardNumber ? 'is-invalid' : ''}`}
                                                        placeholder="Card Number*"
                                                        value={formData.cardNumber}
                                                        onChange={handleInputChange}
                                                        maxLength="19"
                                                        disabled={isButtonDisabled}
                                                    />
                                                    {formErrors.cardNumber && (
                                                        <div className="invalid-feedback">{formErrors.cardNumber}</div>
                                                    )}
                                                </div>

                                                <div className="row">
                                                    <div className="col-md-6 form-group">
                                                        <input
                                                            name="expiryDate"
                                                            type="text"
                                                            className={`form-control ${formErrors.expiryDate ? 'is-invalid' : ''}`}
                                                            placeholder="MM/YY*"
                                                            value={formData.expiryDate}
                                                            onChange={handleInputChange}
                                                            maxLength="5"
                                                            disabled={isButtonDisabled}
                                                        />
                                                        {formErrors.expiryDate && (
                                                            <div className="invalid-feedback">{formErrors.expiryDate}</div>
                                                        )}
                                                    </div>
                                                    <div className="col-md-6 form-group">
                                                        <input
                                                            name="cvv"
                                                            type="text"
                                                            className={`form-control ${formErrors.cvv ? 'is-invalid' : ''}`}
                                                            placeholder="CVV*"
                                                            value={formData.cvv}
                                                            onChange={handleInputChange}
                                                            maxLength="4"
                                                            disabled={isButtonDisabled}
                                                        />
                                                        {formErrors.cvv && (
                                                            <div className="invalid-feedback">{formErrors.cvv}</div>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="form-group">
                                                    <input
                                                        name="cardHolder"
                                                        type="text"
                                                        className={`form-control ${formErrors.cardHolder ? 'is-invalid' : ''}`}
                                                        placeholder="Card Holder Name*"
                                                        value={formData.cardHolder}
                                                        onChange={handleInputChange}
                                                        disabled={isButtonDisabled}
                                                    />
                                                    {formErrors.cardHolder && (
                                                        <div className="invalid-feedback">{formErrors.cardHolder}</div>
                                                    )}
                                                </div>
                                            </>
                                        )}

                                        {/* PayPal Notice */}
                                        {formData.paymentMethod === 'paypal' && (
                                            <div className="paypal-notice">
                                                <div className="alert alert-info">
                                                    <i className="fa-brands fa-paypal me-2"></i>
                                                    You will be redirected to PayPal to complete your payment securely.
                                                </div>
                                            </div>
                                        )}

                                        <div className="form-group mt-4 mb-0">
                                            <button
                                                type="submit"
                                                className="vs-btn payment-submit-btn w-100"
                                                disabled={isButtonDisabled}
                                            >
                                                {processing ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                        Processing Payment...
                                                    </>
                                                ) : (
                                                    `Pay ₹${enquiry.amount || '0.00'}`
                                                )}
                                            </button>
                                        </div>

                                        <div className="payment-terms">
                                            <p className="text-center">
                                                By completing this payment, you agree to our{' '}
                                                <a href="/terms">Terms of Service</a> and{' '}
                                                <a href="/privacy">Privacy Policy</a>
                                            </p>
                                        </div>
                                    </form>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default PaymentPage;